import { serverUrl } from "@/config/default";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import VersionInfo from "@/components/versionInfo";

const TOKEN_KEY = "administratorToken";
type AdminUser = { id: number; login: string; role: string; createdAt: string; testsCount: number };
type Test = { id: number; date: string; approved: boolean | null };
type Part = "audio" | "text" | "image";
type Material = { value: string | null; reconstructed?: boolean; loading?: boolean; error?: string };
type Pagination = { page: number; pageSize: number; total: number; totalPages: number };
type Credentials = { login: string; password: string };
type Api = (path: string, options?: RequestInit) => Promise<any>;

const formatDate = (value: string) => new Date(value).toLocaleString("ru-RU", { dateStyle: "short", timeStyle: "short" });
const statusText = (approved: boolean | null) => approved === true ? "Одобрен" : approved === false ? "Отклонён" : "Ожидает проверки";
const statusClass = (approved: boolean | null) => approved === true ? "approved" : approved === false ? "rejected" : "pending";
const isAborted = (error: any) => error?.name === "AbortError";

const TestContent = ({ test, api, onModerated }: { test: Test; api: Api; onModerated: (approved: boolean) => void }) => {
  const [materials, setMaterials] = useState<Partial<Record<Part, Material>>>({});
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const requests = useRef(new Set<AbortController>());
  useEffect(() => () => { requests.current.forEach((request) => request.abort()); }, []);

  const load = async (part: Part) => {
    if (materials[part]?.loading) return;
    const controller = new AbortController();
    requests.current.add(controller);
    setMaterials((items) => ({ ...items, [part]: { value: null, loading: true } }));
    try {
      const data = await api(`tests/${test.id}?part=${part}`, { signal: controller.signal });
      if (!controller.signal.aborted) setMaterials((items) => ({ ...items, [part]: { value: data[part], reconstructed: data.textReconstructed } }));
    } catch (err) {
      if (!isAborted(err) && !controller.signal.aborted) setMaterials((items) => ({ ...items, [part]: { value: null, error: err.message } }));
    } finally { requests.current.delete(controller); }
  };

  const moderate = async (approved: boolean) => {
    if (saving) return;
    setSaving(true);
    setError("");
    const controller = new AbortController();
    requests.current.add(controller);
    try {
      const data = await api(`tests/${test.id}/moderation`, {
        method: "PUT", body: JSON.stringify({ approved }), signal: controller.signal,
      });
      if (!controller.signal.aborted) {
        onModerated(data.test.approved);
        toast(approved ? "Тест одобрен" : "Тест отклонён");
      }
    } catch (err) {
      if (!isAborted(err) && !controller.signal.aborted) setError(err.message);
    } finally {
      requests.current.delete(controller);
      if (!controller.signal.aborted) setSaving(false);
    }
  };

  return (
    <div className="adminTestContent" aria-busy={saving}>
      {(["audio", "text", "image"] as Part[]).map((part) => {
        const item = materials[part];
        const title = part === "audio" ? "Голос" : part === "text" ? "Текст" : "Отпечаток / образец почерка";
        const action = part === "audio" ? "Прослушать голос" : part === "text" ? "Прочитать текст" : "Просмотреть отпечаток";
        return (
          <section key={part}>
            <h3>{title}</h3>
            {(!item || item.error) && <button className="btn mainBtn compact" onClick={() => load(part)}>{item?.error ? "Повторить загрузку" : action}</button>}
            {item?.loading && <div className="adminMuted" role="status">Чтение результата…</div>}
            {item?.error && <div className="loginError" role="alert">{item.error}</div>}
            {item && !item.loading && !item.error && (
              <>
                {part === "text" && item.reconstructed && <p className="adminMuted">Восстановлен из событий ввода. В старых тестах правки текста могут отображаться неточно.</p>}
                {!item.value ? <div className="adminMuted">{part === "audio" ? "Аудиозапись отсутствует" : part === "text" ? "Текст отсутствует" : "Изображение отсутствует"}</div>
                  : part === "audio" ? <audio controls autoPlay preload="metadata" src={item.value} onError={() => setMaterials((items) => ({ ...items, audio: { value: null, error: "Не удалось воспроизвести запись" } }))} />
                  : part === "text" ? <div className="adminSampleText">{item.value}</div>
                  : <img className="adminSampleImage" src={item.value} alt={`Образец почерка из теста №${test.id}`} onError={() => setMaterials((items) => ({ ...items, image: { value: null, error: "Не удалось открыть изображение" } }))} />}
              </>
            )}
          </section>
        );
      })}
      {error && <div className="loginError" role="alert">{error}</div>}
      <div className="adminActions">
        <button className="btn adminApprove" disabled={saving || test.approved === true} onClick={() => moderate(true)}>Одобрить</button>
        <button className="btn adminReject" disabled={saving || test.approved === false} onClick={() => moderate(false)}>Отклонить</button>
        {saving && <span role="status">Сохранение…</span>}
      </div>
    </div>
  );
};

const UserTests = ({ userId, api }: { userId: number; api: Api }) => {
  const [tests, setTests] = useState<Test[] | null>(null);
  const [error, setError] = useState("");
  const [attempt, setAttempt] = useState(0);
  const [openTests, setOpenTests] = useState<number[]>([]);
  useEffect(() => {
    const controller = new AbortController();
    setError("");
    setTests(null);
    api(`users/${userId}/tests`, { signal: controller.signal })
      .then((data) => { if (!controller.signal.aborted) setTests(data.tests); })
      .catch((err) => { if (!isAborted(err) && !controller.signal.aborted) setError(err.message); });
    return () => controller.abort();
  }, [api, userId, attempt]);

  return (
    <div className="adminTests">
      {error && <div role="alert" className="loginError">{error} <button className="btn linkBtn" onClick={() => setAttempt((a) => a + 1)}>Повторить</button></div>}
      {!tests && !error && <div className="adminMuted" role="status">Загрузка тестов…</div>}
      {tests?.length === 0 && <div className="adminMuted">Пользователь пока не прошёл ни одного теста</div>}
      {tests?.map((test) => {
        const open = openTests.includes(test.id);
        return (
          <article className="adminTestCard" key={test.id}>
            <button className="adminTestToggle btn" aria-expanded={open} aria-controls={`test-${test.id}`} onClick={() => setOpenTests((ids) => open ? ids.filter((id) => id !== test.id) : [...ids, test.id])}>
              <span>{open ? "▾" : "▸"} Тест №{test.id}</span>
              <span>{formatDate(test.date)}</span>
              <span className={`adminStatus ${statusClass(test.approved)}`}>{statusText(test.approved)}</span>
            </button>
            {open && <div id={`test-${test.id}`}><TestContent test={test} api={api} onModerated={(approved) => setTests((items) => items?.map((item) => item.id === test.id ? { ...item, approved } : item) ?? null)} /></div>}
          </article>
        );
      })}
    </div>
  );
};

const AdminPanel = () => {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [access, setAccess] = useState<"checking" | "login" | "ready">(token ? "checking" : "login");
  const [login, setLogin] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [signingIn, setSigningIn] = useState(false);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [refresh, setRefresh] = useState(0);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [creating, setCreating] = useState(false);
  const [credentials, setCredentials] = useState<Credentials | null>(null);
  const [expandedUsers, setExpandedUsers] = useState<number[]>([]);
  const createRequest = useRef<AbortController | null>(null);

  useEffect(() => () => createRequest.current?.abort(), []);

  useEffect(() => {
    document.title = "Администрирование — Скай тест";
    return () => { document.title = "Скай тест"; };
  }, []);

  const logout = useCallback(() => {
    createRequest.current?.abort();
    setCreating(false);
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setAccess("login");
    setUsers([]);
    setPagination(null);
    setCredentials(null);
    setExpandedUsers([]);
    setPage(1);
    setPassword("");
    setError("");
  }, []);

  const api: Api = useCallback(async (endpoint, options = {}) => {
    const res = await fetch(`${serverUrl}administrator/${endpoint}`, {
      ...options,
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}`, ...options.headers },
    });
    const data = await res.json();
    if (res.status === 401 || res.status === 403) {
      logout();
      throw new Error(res.status === 403 ? "Требуется учётная запись администратора" : "Сессия истекла. Войдите снова.");
    }
    if (!res.ok) throw new Error(data.error || "Не удалось выполнить запрос");
    return data;
  }, [token, logout]);

  useEffect(() => {
    if (!token) return;
    const controller = new AbortController();
    setAccess("checking");
    api("session", { signal: controller.signal })
      .then(() => { if (!controller.signal.aborted) setAccess("ready"); })
      .catch((err) => {
        if (!isAborted(err) && !controller.signal.aborted) { setAccess("login"); setError(err.message); }
      });
    return () => controller.abort();
  }, [token, api]);

  useEffect(() => {
    if (access !== "ready") return;
    const controller = new AbortController();
    setLoadingUsers(true);
    setError("");
    setExpandedUsers([]);
    api(`users?page=${page}&pageSize=${pageSize}`, { signal: controller.signal })
      .then((data) => {
        if (controller.signal.aborted) return;
        setUsers(data.users);
        setPagination(data.pagination);
        setPage(data.pagination.page);
      })
      .catch((err) => { if (!isAborted(err) && !controller.signal.aborted) { setUsers([]); setError(err.message); } })
      .finally(() => { if (!controller.signal.aborted) setLoadingUsers(false); });
    return () => controller.abort();
  }, [access, api, page, pageSize, refresh]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSigningIn(true);
    try {
      const res = await fetch(`${serverUrl}administrator/auth`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ login: login.trim(), password }),
      });
      const data = await res.json();
      if (!res.ok || !data.success || data.role !== "admin") throw new Error(data.error || "Требуется учётная запись администратора");
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      setAccess("checking");
      setPassword("");
      setRefresh((r) => r + 1);
    } catch (err) {
      setError(err.message || "Ошибка соединения с сервером");
    } finally {
      setSigningIn(false);
    }
  };

  const createUser = async () => {
    if (creating) return;
    setCreating(true);
    setError("");
    const controller = new AbortController();
    createRequest.current = controller;
    try {
      const data = await api("users", { method: "POST", body: "{}", signal: controller.signal });
      if (controller.signal.aborted) return;
      setCredentials({ login: data.user.login, password: data.user.password });
      toast("Учётная запись создана");
      setRefresh((r) => r + 1);
    } catch (err) {
      if (!isAborted(err) && !controller.signal.aborted) setError(err.message);
    } finally {
      if (!controller.signal.aborted) setCreating(false);
      if (createRequest.current === controller) createRequest.current = null;
    }
  };

  const copyCredentials = async () => {
    if (!credentials) return;
    try {
      await navigator.clipboard.writeText(`${credentials.login}\n${credentials.password}`);
      toast("Логин и пароль скопированы");
    } catch { toast("Скопировать не удалось — сохраните данные вручную"); }
  };

  if (access === "checking") return <div className="page adminPanel gridCenter"><div role="status">Проверка доступа…</div></div>;
  if (access === "login") return (
    <div className="page adminPanel gridCenter">
      <div className="inner columnCenter gap24">
        <h1>Панель администратора</h1>
        <form className="loginForm columnCenter gap12" onSubmit={handleLogin}>
          <label htmlFor="admin-login">Логин администратора</label>
          <input id="admin-login" autoComplete="username" value={login} onChange={(e) => setLogin(e.target.value)} disabled={signingIn} required />
          <label htmlFor="admin-password">Пароль</label>
          <input id="admin-password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} disabled={signingIn} required />
          {error && <div className="loginError" role="alert">{error}</div>}
          <button className="btn mainBtn" disabled={signingIn || !login.trim() || !password}>{signingIn ? "Вход…" : "Войти"}</button>
        </form>
        <Link className="linkBtn" to="/">Вернуться к тесту</Link>
      </div>
      <VersionInfo />
    </div>
  );

  return (
    <div className="page adminPanel">
      <div className="adminInner columnCenter gap24">
        <div className="adminHeader between w100">
          <h1>Пользователи и тесты</h1>
          <div className="adminActions">
            <button className="btn mainBtn compact" onClick={createUser} disabled={creating}>{creating ? "Создание…" : "Создать пользователя"}</button>
            <button className="btn linkBtn" onClick={logout}>Выйти</button>
          </div>
        </div>
        {credentials && (
          <div className="credentialsCard columnCenter gap12">
            <div className="credentialsTitle">Новая учётная запись</div>
            <div className="credentialsRow"><span>Логин (e-mail):</span><b>{credentials.login}</b></div>
            <div className="credentialsRow"><span>Пароль:</span><b>{credentials.password}</b></div>
            <div className="credentialsHint">Пароль показывается один раз — сохраните его и передайте участнику.</div>
            <div className="adminActions">
              <button className="btn mainBtn compact" onClick={copyCredentials}>Скопировать пару</button>
              <button className="btn linkBtn" onClick={() => setCredentials(null)}>Скрыть</button>
            </div>
          </div>
        )}
        <div className="adminHeader between w100">
          <span>{pagination ? `Всего пользователей: ${pagination.total}` : "Список пользователей"}</span>
          <label>На странице: <select value={pageSize} disabled={loadingUsers} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}>
            <option value={10}>10</option><option value={20}>20</option><option value={50}>50</option>
          </select></label>
          <button className="btn linkBtn" disabled={loadingUsers} onClick={() => setRefresh((r) => r + 1)}>Обновить</button>
        </div>
        {error && <div className="loginError" role="alert">{error}</div>}
        {loadingUsers ? <div className="adminMuted" role="status">Загрузка пользователей…</div> : (
          <div className="adminTableWrap w100">
            <table className="adminTable w100">
              <thead><tr><th>ID</th><th>Логин (e-mail)</th><th>Роль</th><th>Тестов</th><th>Создан</th><th>Действия</th></tr></thead>
              <tbody>
                {users.map((user) => {
                  const expanded = expandedUsers.includes(user.id);
                  return (
                    <React.Fragment key={user.id}>
                      <tr className={user.role === "admin" ? "adminRow" : undefined}>
                        <td>{user.id}</td><td>{user.login}</td><td>{user.role === "admin" ? "администратор" : "пользователь"}</td><td>{user.testsCount}</td><td>{formatDate(user.createdAt)}</td>
                        <td><button className="btn linkBtn" aria-expanded={expanded} aria-controls={`user-tests-${user.id}`} onClick={() => setExpandedUsers((ids) => expanded ? ids.filter((id) => id !== user.id) : [...ids, user.id])}>{expanded ? "Свернуть тесты" : "Показать тесты"}</button></td>
                      </tr>
                      {expanded && <tr><td colSpan={6} className="adminTestsCell"><div id={`user-tests-${user.id}`}><UserTests userId={user.id} api={api} /></div></td></tr>}
                    </React.Fragment>
                  );
                })}
                {users.length === 0 && !error && <tr><td colSpan={6} className="adminMuted">Учётных записей пока нет</td></tr>}
              </tbody>
            </table>
          </div>
        )}
        {pagination && <nav className="adminPagination" aria-label="Страницы пользователей">
          <button className="btn mainBtn compact" disabled={loadingUsers || page <= 1} onClick={() => setPage((p) => p - 1)}>Назад</button>
          <span>Страница {pagination.page} из {pagination.totalPages}</span>
          <button className="btn mainBtn compact" disabled={loadingUsers || page >= pagination.totalPages} onClick={() => setPage((p) => p + 1)}>Далее</button>
        </nav>}
        <Link className="linkBtn" to="/">К приложению</Link>
      </div>
      <VersionInfo />
    </div>
  );
};

export default AdminPanel;
